import { useEffect, useState } from 'react'

import { useRouter } from 'next/router'
import { useLocalStorage } from 'usehooks-ts'

const EXPIRATION_TIME = 1000 * 3600 // 1 hour

export type ScrollPositionLocalStorageData = {
    position: number
    showMoreClicked?: boolean
    expiration?: number
}

const useSaveScrollPosition = () => {
    const router = useRouter()
    const localStorageKey = `PERFORMER_PAGE_SCROLL_POSITION_${router.asPath}`

    const [storageValue, setStorageValue] = useLocalStorage<ScrollPositionLocalStorageData>(localStorageKey, {
        position: -1,
    })

    const isListExpandedLocalStorage = Boolean(storageValue?.showMoreClicked)

    const [cannotUpdateScrollPosition, setCannotUpdateScrollPosition] = useState(false)

    const setIsListExpandedLocalStorage = (showMoreClicked: boolean) => {
        setStorageValue((value) => ({ ...value, showMoreClicked }))
    }

    useEffect(() => {
        if (storageValue.expiration && Date.now() > storageValue.expiration) {
            setStorageValue((value) => ({ ...value, position: 0 }))
            localStorage.removeItem(localStorageKey)
            return
        }

        setStorageValue((value) => ({ ...value, expiration: Date.now() + EXPIRATION_TIME }))

        // scroll on page load
        if (storageValue.position) {
            // This is needed, if we try and scroll without waiting it won't work
            setTimeout(() => {
                if (storageValue.position === 0) {
                    return
                }
                window.scrollTo({ top: storageValue.position, behavior: 'smooth' })
            }, 0)
        }
    }, [])

    useEffect(() => {
        // If we don't guard for the route change, a scroll event fires and sets
        // the scrollY property to 0. Adding a guard for scrollY = 0 will
        // cause the page position to not save when a user scrolls back to the top
        // of the page.
        const routeChangeHandler = () => setCannotUpdateScrollPosition(true)

        router.events.on('routeChangeStart', routeChangeHandler)

        return () => router.events.off('routeChangeStart', routeChangeHandler)
    }, [router])

    useEffect(() => {
        const scrollListener = () => {
            if (cannotUpdateScrollPosition) {
                return
            }

            setStorageValue((value) => ({
                ...value,
                position: window.scrollY,
                expiration: Date.now() + EXPIRATION_TIME,
            }))
        }

        window.addEventListener('scrollend', scrollListener)

        return () => window.removeEventListener('scrollend', scrollListener)
    }, [cannotUpdateScrollPosition, setStorageValue])

    return {
        isListExpandedLocalStorage,
        setIsListExpandedLocalStorage,
    }
}

export default useSaveScrollPosition
